import React from "react";
import Image from "next/image";
import Icon from "@/components/icons/Icons";
import { SchemeReturn } from "@/type/schemeType";
import { useLoginModal } from "@/utils/useLoginModal";
import { RollingNavReturns } from "@/type/rollingPageTypes";
import RollingReturnsTable from "../others/RollingReturnsTable";
import RollingReturnChart from "../charts/RollingReturnChart";
import ConsistencyScore from "../others/ConsistencyScore";

const dummyRollingData: RollingNavReturns[] = [
  { startDate: "2010-01-01", endDate: "2013-01-01", returnPercentage: 4.2, startNav: 100, endNav: 104.2 },
  { startDate: "2011-01-01", endDate: "2014-01-01", returnPercentage: 6.8, startNav: 100, endNav: 106.8 },
  { startDate: "2012-01-01", endDate: "2015-01-01", returnPercentage: 9.9, startNav: 100, endNav: 109.9 },
  { startDate: "2013-01-01", endDate: "2016-01-01", returnPercentage: 5.2, startNav: 100, endNav: 105.2 },
  { startDate: "2014-01-01", endDate: "2017-01-01", returnPercentage: 8.5, startNav: 100, endNav: 108.5 },
  { startDate: "2015-01-01", endDate: "2018-01-01", returnPercentage: -2.3, startNav: 100, endNav: 97.7 },
  { startDate: "2016-01-01", endDate: "2019-01-01", returnPercentage: 10.1, startNav: 100, endNav: 110.1 },
  { startDate: "2017-01-01", endDate: "2020-01-01", returnPercentage: 4.7, startNav: 100, endNav: 104.7 },
  { startDate: "2018-01-01", endDate: "2021-01-01", returnPercentage: 12.4, startNav: 100, endNav: 112.4 },
  { startDate: "2019-01-01", endDate: "2022-01-01", returnPercentage: 6.8, startNav: 100, endNav: 106.8 },
  { startDate: "2020-01-01", endDate: "2023-01-01", returnPercentage: 9.9, startNav: 100, endNav: 109.9 },
  { startDate: "2021-01-01", endDate: "2024-01-01", returnPercentage: -1.4, startNav: 100, endNav: 98.6 },
  { startDate: "2022-01-01", endDate: "2025-01-01", returnPercentage: 11.3, startNav: 100, endNav: 111.3 },
  { startDate: "2023-01-01", endDate: "2026-01-01", returnPercentage: 7.6, startNav: 100, endNav: 107.6 },
  { startDate: "2024-01-01", endDate: "2027-01-01", returnPercentage: 13.2, startNav: 100, endNav: 113.2 },
  { startDate: "2025-01-01", endDate: "2028-01-01", returnPercentage: 3.9, startNav: 100, endNav: 103.9 },
  { startDate: "2026-01-01", endDate: "2029-01-01", returnPercentage: -0.8, startNav: 100, endNav: 99.2 },
  { startDate: "2027-01-01", endDate: "2030-01-01", returnPercentage: 8.7, startNav: 100, endNav: 108.7 },
];

export default function DummyRollingSection({ returnData }: { returnData: SchemeReturn }) {
  const { open, setModalKey } = useLoginModal();

  const tableRollingData = [
    {
      label: "This Fund",
      values: {
        "1Y": "00",
        "2Y": "00",
        "3Y": "00",
        "5Y": "00",
      },
    },
    {
      label: "Category Average",
      values: {
        "1Y": "00",
        "2Y": "00",
        "3Y": "00",
        "5Y": "00",
      },
    },
    {
      label: "Benchmark Returns",
      values: {
        "1Y": "00",
        "2Y": "00",
        "3Y": "00",
        "5Y": "00",
      },
    },
  ];

  return (
    <>
      <RollingReturnsTable data={tableRollingData} />

      <RollingReturnChart
        chartData={dummyRollingData}
        schemeName={returnData.scheme_name}
        fundAge={"5y"}
        activePeriod={1}
        onPeriodChange={() => {}}
        isLoggedIn={false}
      />

      <div className="flex flex-col sm:flex-row bg-[#F5F9FF] gap-0 sm:gap-7 items-center justify-between rounded-4xl px-6 py-4 lg:px-9">
        <div className="text-center sm:text-left w-full mb-4 sm:mb-0">
          <div className="font-semibold text-base sm:text-xl text-primaryBlack mb-1 sm:mb-3">
            Return Consistency Score
          </div>
          <p className="font-medium italic text-2xs sm:text-sm text-[#828282]">
            The consistency score is calculated by measuring how often the fund delivers better rolling returns than the
            benchmark over the same time periods.
          </p>
        </div>
        <ConsistencyScore value="100" max={100} />
      </div>

      <div className="absolute inset-0 z-1 bg-white/80 backdrop-blur-[5px] flex items-center justify-center text-center text-sm sm:text-base font-medium text-primaryBlack px-4">
        <div className="m-auto w-fit h-fit">
          <Image
            src={"/next_assets/images/scheme-details/lock-data.webp"}
            alt="Login to unlock Rolling and Discrete Returns"
            width={133}
            height={133}
            className="object-contain mx-auto shrink-0"
          />
          <button
            onClick={() => {
              open();
              setModalKey("rolling-returns");
            }}
            className="group flex items-center gap-4 px-7 py-3 text-base leading-5 border border-primaryBlue text-primaryBlue rounded-xl bg-white hover:bg-primaryBlue hover:text-white transition duration-300 mt-5"
          >
            Unlock Now
            <Icon.dropdownDownArrow className="w-3 h-3 -rotate-90 text-primaryBlue group-hover:text-white transition duration-300" />
          </button>
        </div>
      </div>
    </>
  );
}
